// Web Push (FCM) registration for the EzyDine web app.
//
// - Admin dashboard registers a 'admin' token after login so a backgrounded
//   browser tab still gets new-order pushes.
// - Customer self-order can optionally register a 'customer' token (anon, no
//   Firebase user) so the diner gets a "your food is ready" push if they leave
//   the tab — it complements the in-page OrderConfirmed live-status tracker.
//
// Everything here is best-effort and non-blocking: if the browser doesn't
// support push, the user denies permission, or the VAPID key is missing, the
// calls resolve to null and the app degrades gracefully (the in-page realtime
// status + admin bell keep working).

import { getMessaging, getToken, onMessage, isSupported, type Messaging } from 'firebase/messaging';
import { firebaseApp, firebaseAuth } from './firebase';
import { supabase } from './supabase';

// Web Push VAPID public key — Firebase console → Project settings → Cloud
// Messaging → Web Push certificates → "Key pair". Set it in .env as
// VITE_FIREBASE_VAPID_KEY (see .env.example). Without it web push is disabled.
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

type PushRole = 'admin' | 'customer';

let _messaging: Messaging | null = null;
let _onMessageBound = false;

async function getMessagingOrNull(): Promise<Messaging | null> {
  try {
    if (!(await isSupported())) return null;
    if (!('serviceWorker' in navigator)) return null;
    _messaging ??= getMessaging(firebaseApp);
    return _messaging;
  } catch {
    return null;
  }
}

/** Register the firebase-messaging service worker (served from /public). */
async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  try {
    return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch {
    return null;
  }
}

/**
 * Request notification permission, fetch an FCM web-push token and register it
 * in ezydine.device_tokens. Returns the token, or null if push is unavailable /
 * denied. Safe to call repeatedly — upsert on the token keeps it idempotent.
 *
 * @param role          'admin' (signed-in dashboard) or 'customer' (anon diner)
 * @param restaurantId  the tenant this device belongs to
 * @param onForeground  optional callback for messages received while the tab is
 *                      open/foreground (e.g. show a toast). De-dupe in the UI.
 */
export async function registerPush(
  role: PushRole,
  restaurantId: string,
  onForeground?: (msg: { title?: string; body?: string; data?: Record<string, string> }) => void,
): Promise<string | null> {
  if (!VAPID_KEY || !restaurantId) return null;

  const messaging = await getMessagingOrNull();
  if (!messaging) return null;

  // Ask for permission only if not already decided.
  if (Notification.permission === 'default') {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return null;
    } catch {
      return null;
    }
  }
  if (Notification.permission !== 'granted') return null;

  const swReg = await registerSW();
  if (!swReg) return null;

  let token: string | null = null;
  try {
    token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
  } catch {
    return null;
  }
  if (!token) return null;

  // For admin we attach the Firebase uid; customers are anonymous (uid null),
  // matching the device_tokens RLS policies in supabase/push-notifications.sql.
  const userId = role === 'admin' ? (firebaseAuth.currentUser?.uid ?? null) : null;

  try {
    await supabase
      .from('device_tokens')
      .upsert(
        {
          restaurantId,
          userId,
          role,
          token,
          platform: 'web',
          updatedAt: new Date().toISOString(),
        },
        { onConflict: 'token' },
      );
  } catch {
    // Token registration failed (RLS / network) — push simply won't arrive;
    // the in-app realtime feeds still work.
    return token;
  }

  // Foreground messages: the SW only fires for background tabs, so surface
  // anything that lands while the tab is open via the caller's handler.
  if (onForeground && !_onMessageBound) {
    _onMessageBound = true;
    onMessage(messaging, (payload) => {
      onForeground({
        title: payload.notification?.title,
        body: payload.notification?.body,
        data: payload.data as Record<string, string> | undefined,
      });
    });
  }

  return token;
}

/** Remove this browser's token (call on admin sign-out). Best-effort. */
export async function unregisterPush(token: string | null): Promise<void> {
  if (!token) return;
  try {
    await supabase.from('device_tokens').delete().eq('token', token);
  } catch {
    /* ignore */
  }
}
